import * as React from 'react';
import { useState, useEffect } from 'react';

// Joy UI Components
import {
    Breadcrumbs,
    Link,
    Box,
    Typography,
    Button,
    Card,
    IconButton,
    Modal,
    ModalDialog,
    ModalClose,
    DialogTitle,
    DialogContent,
    DialogActions,
    Input,
    Stack,
    Divider,
    Sheet,
    Grid,
    CircularProgress,
    Alert,
    FormLabel,
    Switch,
    Table,
    Chip,
} from "@mui/joy";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';

// Icons
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BlockIcon from '@mui/icons-material/Block';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';

// API Service
import GeneralApiService, {
    GetFranchiseVans,
    CreateFranchiseVan,
    UpdateFranchiseVan,
    DeleteFranchiseVan,
    CreateVanBlockedDate,
    DeleteVanBlockedDate,
    UpdateVanBlockedDate,
    CreateVanDayOverride,
    DeleteVanDayOverride,
} from '../../axios/Axios';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const JS_DAY_TO_NAME = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function Vans() {
    const [vans, setVans] = useState([]);
    const [franchiseId, setFranchiseId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [addVanModal, setAddVanModal] = useState(false);
    const [deleteVanModal, setDeleteVanModal] = useState(false);
    const [blockDateModal, setBlockDateModal] = useState(false);

    const [newVanName, setNewVanName] = useState('');
    const [selectedVan, setSelectedVan] = useState(null);
    const [blockStartDate, setBlockStartDate] = useState('');
    const [blockEndDate, setBlockEndDate] = useState('');
    const [blockReason, setBlockReason] = useState('');
    const [showPastDates, setShowPastDates] = useState(false);
    const [editingReasons, setEditingReasons] = useState({});

    useEffect(() => {
        const fetchFranchiseAndVans = async () => {
            try {
                setLoading(true);
                setError('');
                const franchise = await GeneralApiService.getFranchiseByName();
                setFranchiseId(franchise.id);

                const vanData = await GetFranchiseVans(franchise.id);

                // --- THIS IS THE FIX ---
                // We access vanData.data because the ApiService returns the full Axios response
                const formattedVans = vanData.data.map(van => ({
                    id: van.id,
                    name: van.name,
                    weeklySchedule: van.weekly_schedule,
                    roofDaysMinimum: van.roof_days_minimum ?? 1,
                    roofDaysSidesModifier: van.roof_days_sides_modifier ?? 2,
                    dayOverrides: (van.day_overrides || []).map(override => ({
                        id: override.id,
                        date: override.date,
                        isWorkingOverride: override.is_working_override,
                        reason: override.reason,
                    })),
                    blockedDates: van.blocked_dates.map(block => ({
                        id: block.id,
                        startDate: block.start_date,
                        endDate: block.end_date,
                        reason: block.reason
                    }))
                }));

                setVans(formattedVans.sort((a, b) => a.id - b.id));
            } catch (err) {
                console.error("Failed to fetch data:", err);
                setError('Failed to load van data. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchFranchiseAndVans();
    }, []);

    // Add new van
    const handleAddVan = async () => {
        if (!newVanName.trim() || !franchiseId) return;

        try {
            const response = await CreateFranchiseVan(franchiseId, newVanName);
            const newVanData = response.data;

            const formattedNewVan = {
                id: newVanData.id,
                name: newVanData.name,
                weeklySchedule: newVanData.weekly_schedule,
                roofDaysMinimum: newVanData.roof_days_minimum ?? 1,
                roofDaysSidesModifier: newVanData.roof_days_sides_modifier ?? 2,
                dayOverrides: [],
                blockedDates: []
            };
            setVans([...vans, formattedNewVan].sort((a, b) => a.id - b.id));
            setNewVanName('');
            setAddVanModal(false);
        } catch (err) {
            console.error("Failed to add van:", err);
        }
    };

    // Delete van
    const handleDeleteVan = async () => {
        if(vans.length == 1) return;
        if (!selectedVan) return;

        try {
            await DeleteFranchiseVan(selectedVan.id);
            setVans(vans.filter(v => v.id !== selectedVan.id));
            setDeleteVanModal(false);
            setSelectedVan(null);
        } catch (err) {
            console.error("Failed to delete van:", err);
        }
    };

    // Toggle day availability
    const toggleDay = async (vanId, day) => {
        const van = vans.find(v => v.id === vanId);
        if (!van) return;

        const updatedSchedule = {
            ...van.weeklySchedule,
            [day]: !van.weeklySchedule[day]
        };

        try {
            await UpdateFranchiseVan(vanId, { weekly_schedule: updatedSchedule });
            setVans(vans.map(v => v.id === vanId ? { ...v, weeklySchedule: updatedSchedule } : v));
        } catch (err) {
            console.error("Failed to update schedule:", err);
        }
    };

    const updateRoofDaysConfig = async (vanId, updates) => {
        const van = vans.find(v => v.id === vanId);
        if (!van) return;

        const payload = {
            roof_days_minimum: updates.roofDaysMinimum ?? van.roofDaysMinimum,
            roof_days_sides_modifier: updates.roofDaysSidesModifier ?? van.roofDaysSidesModifier,
        };

        try {
            await UpdateFranchiseVan(vanId, payload);
            setVans(vans.map(v => v.id === vanId ? { ...v, ...updates } : v));
        } catch (err) {
            console.error("Failed to update roof day settings:", err);
        }
    };

    // Add blocked date range
    const handleAddBlockedDate = async () => {
        if (!blockStartDate || !blockEndDate || !selectedVan) return;

        const payload = {
            start_date: blockStartDate,
            end_date: blockEndDate,
            reason: blockReason || 'Unavailable'
        };

        try {
            const response = await CreateVanBlockedDate(selectedVan.id, payload);
            const newBlockData = response.data;

            const formattedNewBlock = {
                id: newBlockData.id,
                startDate: newBlockData.start_date,
                endDate: newBlockData.end_date,
                reason: newBlockData.reason
            };

            setVans(vans.map(van =>
                van.id === selectedVan.id
                    ? { ...van, blockedDates: [...van.blockedDates, formattedNewBlock].sort((a,b) => new Date(a.startDate) - new Date(b.startDate)) }
                    : van
            ));

            setBlockStartDate('');
            setBlockEndDate('');
            setBlockReason('');
            setBlockDateModal(false);
            setSelectedVan(null);
        } catch (err) {
            console.error("Failed to add blocked date:", err);
        }
    };

    const formatDateForDisplay = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(`${dateStr}T00:00:00`);
        return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const toDateKey = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const isDateBlockedByRange = (van, date) => {
        const dateKey = toDateKey(date);
        return van.blockedDates.some((block) => block.startDate <= dateKey && dateKey <= block.endDate);
    };

    const getOverrideByDateKey = (van, dateKey) => van.dayOverrides?.find((override) => override.date === dateKey);

    const getBaselineAvailability = (van, date) => {
        const dayName = JS_DAY_TO_NAME[date.getDay()];
        if (isDateBlockedByRange(van, date)) return false;
        return !!van.weeklySchedule?.[dayName];
    };

    const getEffectiveAvailability = (van, date) => {
        const dateKey = toDateKey(date);
        const override = getOverrideByDateKey(van, dateKey);
        if (override) return !!override.isWorkingOverride;
        return getBaselineAvailability(van, date);
    };

    const handleCalendarDateToggle = async (van, date) => {
        const dateKey = toDateKey(date);
        const currentlyWorking = getEffectiveAvailability(van, date);
        const targetWorking = !currentlyWorking;
        const baselineWorking = getBaselineAvailability(van, date);
        const existingOverride = getOverrideByDateKey(van, dateKey);

        try {
            if (existingOverride && targetWorking === baselineWorking) {
                await DeleteVanDayOverride(van.id, existingOverride.id);
                setVans(prev => prev.map(v => (
                    v.id === van.id
                        ? { ...v, dayOverrides: v.dayOverrides.filter(o => o.id !== existingOverride.id) }
                        : v
                )));
                return;
            }

            const response = await CreateVanDayOverride(van.id, {
                date: dateKey,
                is_working_override: targetWorking,
                reason: targetWorking ? 'Manual working day override' : 'Manual off day override',
            });

            const savedOverride = {
                id: response.data.id,
                date: response.data.date,
                isWorkingOverride: response.data.is_working_override,
                reason: response.data.reason,
            };

            setVans(prev => prev.map(v => {
                if (v.id !== van.id) return v;
                const withoutSameDate = v.dayOverrides.filter(o => o.date !== savedOverride.date);
                return { ...v, dayOverrides: [...withoutSameDate, savedOverride] };
            }));
        } catch (err) {
            console.error("Failed to toggle day override:", err);
        }
    };

    const todayKey = toDateKey(new Date());

    const nextDay = (dateKey) => {
        const d = new Date(`${dateKey}T00:00:00`);
        d.setDate(d.getDate() + 1);
        return toDateKey(d);
    };

    const cleanOverrideReason = (reason) =>
        (reason && reason !== 'Manual off day override') ? reason : '';

    const getOffDayRows = (van) => {
        const rows = [];

        const offOverrides = (van.dayOverrides || [])
            .filter(o => !o.isWorkingOverride)
            .sort((a, b) => a.date.localeCompare(b.date));

        const groups = [];
        for (const override of offOverrides) {
            const last = groups[groups.length - 1];
            if (last && nextDay(last[last.length - 1].date) === override.date) {
                last.push(override);
            } else {
                groups.push([override]);
            }
        }

        for (const group of groups) {
            const firstDate = group[0].date;
            const lastDate = group[group.length - 1].date;
            const isSingle = group.length === 1;
            const reason = cleanOverrideReason(
                group.find(o => cleanOverrideReason(o.reason))?.reason || group[0].reason
            );
            rows.push({
                key: `override-group-${firstDate}`,
                type: 'override-group',
                overrides: group,
                sortDate: firstDate,
                endDate: lastDate,
                dateLabel: isSingle
                    ? formatDateForDisplay(firstDate)
                    : `${formatDateForDisplay(firstDate)} – ${formatDateForDisplay(lastDate)}`,
                reason,
            });
        }

        (van.blockedDates || []).forEach((block) => {
            const isSingleDay = block.startDate === block.endDate;
            const reason = (block.reason && block.reason !== 'Unavailable') ? block.reason : '';
            rows.push({
                key: `blocked-${block.id}`,
                type: 'blocked',
                id: block.id,
                sortDate: block.startDate,
                endDate: block.endDate,
                dateLabel: isSingleDay
                    ? formatDateForDisplay(block.startDate)
                    : `${formatDateForDisplay(block.startDate)} – ${formatDateForDisplay(block.endDate)}`,
                reason,
            });
        });

        rows.sort((a, b) => a.sortDate.localeCompare(b.sortDate));

        if (!showPastDates) {
            return rows.filter((row) => {
                const relevantDate = row.endDate || row.sortDate;
                return relevantDate >= todayKey;
            });
        }

        return rows;
    };

    const handleUpdateOverrideGroupReason = async (van, overrides, newReason) => {
        try {
            await Promise.all(overrides.map(o =>
                CreateVanDayOverride(van.id, {
                    date: o.date,
                    is_working_override: false,
                    reason: newReason,
                })
            ));
            const dateSet = new Set(overrides.map(o => o.date));
            setVans(prev => prev.map(v => {
                if (v.id !== van.id) return v;
                return {
                    ...v,
                    dayOverrides: v.dayOverrides.map(o =>
                        dateSet.has(o.date) ? { ...o, reason: newReason } : o
                    ),
                };
            }));
        } catch (err) {
            console.error("Failed to update override group reason:", err);
        }
    };

    const handleUpdateBlockedReason = async (van, block, newReason) => {
        try {
            await UpdateVanBlockedDate(van.id, block.id, { reason: newReason });
            setVans(prev => prev.map(v => {
                if (v.id !== van.id) return v;
                return {
                    ...v,
                    blockedDates: v.blockedDates.map(b =>
                        b.id === block.id ? { ...b, reason: newReason } : b
                    ),
                };
            }));
        } catch (err) {
            console.error("Failed to update blocked date reason:", err);
        }
    };

    const handleDeleteOverrideGroup = async (van, overrides) => {
        try {
            await Promise.all(overrides.map(o => DeleteVanDayOverride(van.id, o.id)));
            const idSet = new Set(overrides.map(o => o.id));
            setVans(prev => prev.map(v => {
                if (v.id !== van.id) return v;
                return { ...v, dayOverrides: v.dayOverrides.filter(o => !idSet.has(o.id)) };
            }));
        } catch (err) {
            console.error("Failed to delete override group:", err);
        }
    };

    const handleDeleteBlockedDate = async (van, blockId) => {
        try {
            await DeleteVanBlockedDate(van.id, blockId);
            setVans(prev => prev.map(v => {
                if (v.id !== van.id) return v;
                return { ...v, blockedDates: v.blockedDates.filter(b => b.id !== blockId) };
            }));
        } catch (err) {
            console.error("Failed to delete blocked date:", err);
        }
    };

    const handleReasonBlur = (van, row) => {
        const editKey = `${van.id}-${row.key}`;
        const newReason = editingReasons[editKey];
        if (newReason === undefined || newReason === row.reason) {
            setEditingReasons(prev => {
                const next = { ...prev };
                delete next[editKey];
                return next;
            });
            return;
        }

        if (row.type === 'override-group') {
            handleUpdateOverrideGroupReason(van, row.overrides, newReason);
        } else {
            const block = van.blockedDates.find(b => b.id === row.id);
            if (block) handleUpdateBlockedReason(van, block, newReason);
        }

        setEditingReasons(prev => {
            const next = { ...prev };
            delete next[editKey];
            return next;
        });
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress size="lg" /></Box>;
    }

    if (error) {
        return <Alert color="danger" startDecorator={<WarningIcon />}>{error}</Alert>;
    }

    return (
        <>
            {/* BREADCRUMBS */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs
                    size="sm"
                    aria-label="breadcrumbs"
                    separator={<ChevronRightRoundedIcon />}
                    sx={{ pl: 0 }}
                >
                    <Link
                        underline="none"
                        color="neutral"
                        href="/dashboard"
                        aria-label="Home"
                    >
                        <HomeRoundedIcon />
                    </Link>
                    <Link
                        underline="hover"
                        color="neutral"
                        href="/dashboard"
                        fontSize={12}
                        fontWeight={500}
                    >
                        Dashboard
                    </Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>
                        Vans
                    </Typography>
                </Breadcrumbs>
            </Box>

            {/* PAGE HEADER */}
            <Box
                sx={{
                    display: 'flex',
                    mb: 3,
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 0 },
                }}
            >
                <Typography level="h2" component="h1" sx={{ fontSize: { xs: 'xl', sm: 'xl2' } }}>
                    Van Schedule Management
                </Typography>
                <Button
                    startDecorator={<AddIcon />}
                    onClick={() => setAddVanModal(true)}
                    color="primary"
                    size="sm"
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Add Van
                </Button>
            </Box>

            {/* VAN CARDS */}
            <Stack spacing={3}>
                {vans.map((van) => (
                    <Card key={van.id} variant="outlined" sx={{ p: 3 }}>
                        {/* Van Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocalShippingIcon color="primary" />
                                <Typography level="h4">{van.name}</Typography>
                            </Box>
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="danger"
                                onClick={() => {
                                    setSelectedVan(van);
                                    setDeleteVanModal(true);
                                }}
                            >
                                <DeleteOutlineIcon />
                            </IconButton>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Roof Day Rules */}
                        <Typography level="title-md" sx={{ mb: 1 }}>
                            Roof Clean Day Rules
                        </Typography>
                        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
                            <Box>
                                <FormLabel sx={{ mb: 0.5 }}>Minimum Days To Complete</FormLabel>
                                <Input
                                    type="number"
                                    value={van.roofDaysMinimum}
                                    slotProps={{ input: { min: 1, step: 1 } }}
                                    sx={{ backgroundColor: '#fff' }}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        setVans(prev => prev.map(v => (
                                            v.id === van.id ? { ...v, roofDaysMinimum: isNaN(value) ? 1 : Math.max(1, value) } : v
                                        )));
                                    }}
                                    onBlur={() => updateRoofDaysConfig(van.id, { roofDaysMinimum: van.roofDaysMinimum })}
                                />
                                <Typography level="body-xs" sx={{ mt: 0.75, color: 'text.secondary' }}>
                                    Minimum days for all roof cleans
                                </Typography>
                            </Box>
                            <Box>
                                <FormLabel sx={{ mb: 0.5 }}>Sides Modifier (sides per day)</FormLabel>
                                <Input
                                    type="number"
                                    value={van.roofDaysSidesModifier}
                                    slotProps={{ input: { min: 0.1, step: 0.1 } }}
                                    sx={{ backgroundColor: '#fff' }}
                                    onChange={(e) => {
                                        const value = parseFloat(e.target.value);
                                        setVans(prev => prev.map(v => (
                                            v.id === van.id ? { ...v, roofDaysSidesModifier: isNaN(value) ? 2 : Math.max(0.1, value) } : v
                                        )));
                                    }}
                                    onBlur={() => updateRoofDaysConfig(van.id, { roofDaysSidesModifier: van.roofDaysSidesModifier })}
                                />
                                <Typography level="body-xs" sx={{ mt: 0.75, color: 'text.secondary' }}>
                                    Formula: max(minimum days, ceil(sides / modifier)). Live example with current values:{' '}
                                    2 sides = {Math.max(van.roofDaysMinimum || 1, Math.ceil(2 / (van.roofDaysSidesModifier || 2)))} day(s),{' '}
                                    5 sides = {Math.max(van.roofDaysMinimum || 1, Math.ceil(5 / (van.roofDaysSidesModifier || 2)))} day(s).
                                </Typography>
                            </Box>
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        {/* Weekly Schedule */}
                        <Typography level="title-md" sx={{ mb: 2 }}>
                            Weekly Schedule
                        </Typography>
                        <Grid container spacing={1} sx={{ mb: 3 }}>
                            {DAYS.map((day) => (
                                <Grid key={day} xs={12} sm={6} md={3} lg={1.71}>
                                    <Sheet
                                        variant="soft"
                                        color={van.weeklySchedule[day] ? 'success' : 'danger'}
                                        sx={{
                                            p: 2,
                                            borderRadius: 'sm',
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: 'sm'
                                            }
                                        }}
                                        onClick={() => toggleDay(van.id, day)}
                                    >
                                        <Typography level="title-sm" sx={{ mb: 0.5 }}>
                                            {day.slice(0, 3)}
                                        </Typography>
                                        <Typography level="body-xs">
                                            {van.weeklySchedule[day] ? 'Available' : 'Off'}
                                        </Typography>
                                    </Sheet>
                                </Grid>
                            ))}
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography level="title-md">
                                Availability Calendar
                            </Typography>
                            <Button
                                size="sm"
                                variant="outlined"
                                startDecorator={<BlockIcon />}
                                onClick={() => {
                                    setSelectedVan(van);
                                    setBlockDateModal(true);
                                }}
                            >
                                Block Dates
                            </Button>
                        </Box>
                        <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.5 }}>
                            Click a day to toggle it. Green means working day, red means off day.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>
                            <Box sx={{ display: 'inline-block', border: '1px solid', borderColor: 'divider', borderRadius: 'sm', p: 1, flexShrink: 0 }}>
                                <DatePicker
                                    inline
                                    selected={null}
                                    onChange={(date) => {
                                        if (date) {
                                            handleCalendarDateToggle(van, date);
                                        }
                                    }}
                                    dayClassName={(date) => (
                                        getEffectiveAvailability(van, date) ? 'van-day-working' : 'van-day-off'
                                    )}
                                />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography level="title-sm">Off Day Notes</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Show past</Typography>
                                        <Switch
                                            size="sm"
                                            checked={showPastDates}
                                            onChange={(e) => setShowPastDates(e.target.checked)}
                                        />
                                    </Box>
                                </Box>
                                {(() => {
                                    const rows = getOffDayRows(van);
                                    if (rows.length === 0) {
                                        return (
                                            <Sheet variant="soft" sx={{ p: 2, borderRadius: 'sm', textAlign: 'center' }}>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                    No off days {showPastDates ? '' : 'upcoming'}. Click dates on the calendar or use Block Dates to add them.
                                                </Typography>
                                            </Sheet>
                                        );
                                    }
                                    return (
                                        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: 280 }}>
                                            <Table size="sm" stickyHeader sx={{ '& th': { py: 0.75 }, '& td': { py: 0.5 } }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '35%' }}>Date(s)</th>
                                                        <th>Notes</th>
                                                        <th style={{ width: 40 }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows.map((row) => {
                                                        const editKey = `${van.id}-${row.key}`;
                                                        const editValue = editingReasons[editKey];
                                                        return (
                                                            <tr key={row.key}>
                                                                <td>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                        <Typography level="body-xs" noWrap>{row.dateLabel}</Typography>
                                                                        {row.type === 'blocked' && (
                                                                            <Chip size="sm" variant="soft" color="danger" sx={{ fontSize: '0.6rem', minHeight: 18, height: 18 }}>
                                                                                Blocked
                                                                            </Chip>
                                                                        )}
                                                                    </Box>
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        size="sm"
                                                                        variant="plain"
                                                                        placeholder="Add a note..."
                                                                        value={editValue !== undefined ? editValue : row.reason}
                                                                        onChange={(e) => setEditingReasons(prev => ({ ...prev, [editKey]: e.target.value }))}
                                                                        onBlur={() => handleReasonBlur(van, row)}
                                                                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                                        sx={{ fontSize: '0.75rem', '--Input-minHeight': '28px' }}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <IconButton
                                                                        size="sm"
                                                                        variant="plain"
                                                                        color="neutral"
                                                                        onClick={() => {
                                                                            if (row.type === 'override-group') {
                                                                                handleDeleteOverrideGroup(van, row.overrides);
                                                                            } else {
                                                                                handleDeleteBlockedDate(van, row.id);
                                                                            }
                                                                        }}
                                                                        sx={{ '--IconButton-size': '24px' }}
                                                                    >
                                                                        <CloseIcon sx={{ fontSize: 14 }} />
                                                                    </IconButton>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </Sheet>
                                    );
                                })()}
                            </Box>
                        </Box>
                    </Card>
                ))}

                {vans.length === 0 && !loading && (
                    <Card variant="soft" sx={{ p: 4, textAlign: 'center' }}>
                        <LocalShippingIcon sx={{ fontSize: 48, color: 'text.tertiary', mb: 2 }} />
                        <Typography level="body-lg" sx={{ color: 'text.tertiary' }}>
                            No vans yet. Click "Add Van" to get started.
                        </Typography>
                    </Card>
                )}
            </Stack>

            {/* ADD VAN MODAL */}
            <Modal open={addVanModal} onClose={() => setAddVanModal(false)}>
                <ModalDialog sx={{ 
                    width: { xs: '95vw', sm: 'auto' },
                    maxWidth: { xs: '95vw', sm: '400px' },
                }}>
                    <ModalClose />
                    <DialogTitle>Add New Van</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <Input
                                placeholder="Enter van name (e.g., Van 002)"
                                value={newVanName}
                                onChange={(e) => setNewVanName(e.target.value)}
                                autoFocus
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="solid" color="primary" onClick={handleAddVan}>
                            Add Van
                        </Button>
                        <Button variant="plain" color="neutral" onClick={() => setAddVanModal(false)}>
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

            {/* DELETE VAN MODAL */}
            <Modal open={deleteVanModal} onClose={() => setDeleteVanModal(false)}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ 
                    width: { xs: '95vw', sm: 'auto' },
                    maxWidth: { xs: '95vw', sm: '400px' },
                }}>
                    <ModalClose />
                    <DialogTitle>
                        <DeleteOutlineIcon />
                        Confirm Deletion
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        Are you sure you want to delete {selectedVan?.name}? This action cannot be undone.
                    </DialogContent>
                    <DialogActions>
                        <Button variant="solid" color="danger" onClick={handleDeleteVan}>
                            Delete Van
                        </Button>
                        <Button variant="plain" color="neutral" onClick={() => setDeleteVanModal(false)}>
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

            {/* BLOCK DATE MODAL */}
            <Modal open={blockDateModal} onClose={() => {setBlockDateModal(false); setSelectedVan(null);}}>
                <ModalDialog sx={{ 
                    width: { xs: '95vw', sm: 'auto' },
                    maxWidth: { xs: '95vw', sm: '400px' },
                }}>
                    <ModalClose />
                    <DialogTitle>Block Date Range for {selectedVan?.name}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <Box>
                                <Typography level="body-sm" sx={{ mb: 1 }}>
                                    Start Date
                                </Typography>
                                <Input
                                    type="date"
                                    value={blockStartDate}
                                    onChange={(e) => setBlockStartDate(e.target.value)}
                                />
                            </Box>
                            <Box>
                                <Typography level="body-sm" sx={{ mb: 1 }}>
                                    End Date
                                </Typography>
                                <Input
                                    type="date"
                                    value={blockEndDate}
                                    onChange={(e) => setBlockEndDate(e.target.value)}
                                />
                            </Box>
                            <Box>
                                <Typography level="body-sm" sx={{ mb: 1 }}>
                                    Reason (optional)
                                </Typography>
                                <Input
                                    placeholder="e.g., Maintenance, Holiday"
                                    value={blockReason}
                                    onChange={(e) => setBlockReason(e.target.value)}
                                />
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="solid" color="primary" onClick={handleAddBlockedDate}>
                            Block Dates
                        </Button>
                        <Button variant="plain" color="neutral" onClick={() => {setBlockDateModal(false); setSelectedVan(null);}}>
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

        </>
    );
}